import type { FC } from 'react';
import type { PassCredits, PassPeriod } from '@/types';
import { getPassMeta, type PassTimeStatus } from '@/shared/lib/client';
import styles from './CreditBadge.module.css';

interface CreditBadgeProps {
  pass: PassPeriod & { credits?: PassCredits | null };
}

const LOW_CREDITS_RATIO = 0.2;

const getTone = (
  remainingCredits: number | null,
  maxCredits: number | null,
  timeStatus: PassTimeStatus
): 'neutral' | 'warning' | 'destructive' => {
  if (timeStatus === 'expired' || remainingCredits === 0) return 'destructive';
  if (timeStatus !== 'active' || remainingCredits === null) return 'neutral';
  if (maxCredits !== null && maxCredits > 0 && remainingCredits / maxCredits < LOW_CREDITS_RATIO) {
    return 'warning';
  }
  return 'neutral';
};

const formatNumber = (value: number): string => new Intl.NumberFormat('en-US').format(value);

export const CreditBadge: FC<CreditBadgeProps> = ({ pass }) => {
  const meta = getPassMeta(pass);
  const timeStatus = meta?.timeStatus ?? 'active';
  const remainingCredits = meta?.remainingCredits ?? null;
  const maxCredits = pass.credits?.maxCredits ?? null;
  const tone = getTone(remainingCredits, maxCredits, timeStatus);
  return (
    <span className={styles.balance} data-tone={tone}>
      <span className={styles.value}>
        {remainingCredits === null ? (
          <span role="img" aria-label="Unlimited">
            ∞
          </span>
        ) : (
          formatNumber(remainingCredits)
        )}
      </span>{' '}
      <span className={styles.label}>{remainingCredits === 1 ? 'visit' : 'visits'}</span>
    </span>
  );
};
